// @flow

import { put, call, all, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import * as actions from "@modules/service/constants/actions";
import actionCreators from "@modules/service/actionCreator";
import type {
  NewEmailNotification,
  EmailNotification,
} from "@modules/service/reducer/email-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type EmailNotificationsListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetEmailNotifications({
  payload,
}: Action<EmailNotificationsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<EmailNotification>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process`,
    );

    yield put(actionCreators.GetEmailNotificationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmailNotificationsError(e.response));
  }
}

type EmailNotificationsItemRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onGetEmailNotification({
  payload,
}: Action<EmailNotificationsItemRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<EmailNotification>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.ruleId}`,
    );

    yield put(actionCreators.GetEmailNotificationSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmailNotificationError(e.response));
  }
}

type EmailNoficationSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetEmailNotificationSchema({
  payload,
}: Action<EmailNoficationSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("EmailNotificationProcessDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetEmailNotificationSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEmailNotificationSchemaError(e.response));
  }
}

type AddEmailNotificationRequest = $Exact<{
  clientId: string,
  rule: NewEmailNotification,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onAddEmailNotification({
  payload,
}: Action<AddEmailNotificationRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process`,
      payload.rule,
    );
    if (!response || !response.data) {
      throw Error("Failed to create new rule");
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddEmailNotificationSuccess()),
      put(actionCreators.GetEmailNotifications({ clientId: payload.clientId })),
      put(actionCreators.SetShowAddEmailNotificationModal(false)),
      put(sendSuccessMessage("EMAIL_NOTIFICATION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmailNotificationError(e.response));
  }
}

type UpdateEmailNotificationRequest = $Exact<{
  clientId: string,
  rule: EmailNotification,
  assignments: Array<HierarchyAssignment>,
  isAssigmentsRequired: boolean,
}>;

export function* onUpdateEmailNotification({
  payload,
}: Action<UpdateEmailNotificationRequest>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.rule ||
      (payload.isAssigmentsRequired && !payload.assignments)
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.rule.id}`,
      payload.rule,
    );

    if (!payload.rule.isDefault) {
      const { data: prevAssignments } = yield call(
        http.get,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.rule.id}/assignment`,
      );
      if (payload.isAssigmentsRequired && prevAssignments && prevAssignments.length) {
        yield all([
          ...prevAssignments.map((a: HierarchyAssignment) => {
            return call(
              http.delete,
              `/api/v1/service-admin/client/${
                payload.clientId
              }/service-config/absence-management/email-notification/process/${
                payload.rule.id
              }/assignment/${a.id || ""}`,
            );
          }),
        ]);
      }

      if (payload.isAssigmentsRequired && payload.assignments.length) {
        yield all([
          ...payload.assignments.map((a: HierarchyAssignment) => {
            return call(
              http.post,
              `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.rule.id}/assignment`,
              a,
            );
          }),
        ]);
      }
    }

    yield all([
      put(actionCreators.UpdateEmailNotificationSuccess()),
      put(actionCreators.GetEmailNotifications({ clientId: payload.clientId })),
      put(
        actionCreators.GetEmailNotification({
          clientId: payload.clientId,
          ruleId: payload.rule.id,
        }),
      ),
      put(actionCreators.SetShowEditEmailNotificationModal({ show: false })),
      put(sendSuccessMessage("EMAIL_NOTIFICATION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmailNotificationError(e.response));
  }
}

type DeleteEmailNotificationRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onDeleteEmailNotification({
  payload,
}: Action<DeleteEmailNotificationRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.ruleId}`,
    );

    yield all([
      put(actionCreators.DeleteEmailNotificationSuccess()),
      put(actionCreators.GetEmailNotifications({ clientId: payload.clientId })),
      put(actionCreators.SetShowDeleteEmailNotificationModal(false)),
      put(sendSuccessMessage("EMAIL_NOTIFICATION_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteEmailNotificationError(e.response));
  }
}

type EmailNotificationAssignmentsRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onGetEmailNotificationAssignments({
  payload,
}: Action<EmailNotificationAssignmentsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<HierarchyAssignment>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/email-notification/process/${payload.ruleId}/assignment`,
    );

    yield put(actionCreators.GetEmailNotificationAssignmentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmailNotificationAssignmentsError(e.response));
  }
}

// ROOT

export function* EmailNotificationsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetEmailNotifications, onGetEmailNotifications),
    takeEvery(actions.GetEmailNotification, onGetEmailNotification),
    takeEvery(actions.GetEmailNotificationSchema, onGetEmailNotificationSchema),
    takeEvery(actions.AddEmailNotification, onAddEmailNotification),
    takeEvery(actions.UpdateEmailNotification, onUpdateEmailNotification),
    takeEvery(actions.DeleteEmailNotification, onDeleteEmailNotification),
    takeEvery(actions.GetEmailNotificationAssignments, onGetEmailNotificationAssignments),
  ]);
}

export default EmailNotificationsSagas;
